/**
 * Client API base per comunicare con il backend
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

// Token storage
let accessToken: string | null = localStorage.getItem('accessToken')
let refreshToken: string | null = localStorage.getItem('refreshToken')

/**
 * Salva i token
 */
export function setTokens(access: string, refresh: string) {
  accessToken = access
  refreshToken = refresh
  localStorage.setItem('accessToken', access)
  localStorage.setItem('refreshToken', refresh)
}

/**
 * Rimuove i token (logout)
 */
export function clearTokens() {
  accessToken = null
  refreshToken = null
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

/**
 * Ottiene l'access token corrente
 */
export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Verifica se l'utente è autenticato
 */
export function isAuthenticated(): boolean {
  return !!accessToken
}

/**
 * Rinnova l'access token usando il refresh token.
 * Usa un mutex per evitare race condition con token rotation:
 * se più richieste tentano il refresh simultaneamente,
 * solo la prima esegue realmente la chiamata.
 */
let refreshPromise: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshToken) return false

  // Se un refresh è già in corso, attendi il suo risultato
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) {
        clearTokens()
        return false
      }

      const data = await response.json()
      setTokens(data.accessToken, data.refreshToken)
      return true
    } catch {
      clearTokens()
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Funzione fetch wrapper con gestione automatica token
 */
export async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Aggiungi token se disponibile
  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`
  }

  let response = await fetch(url, { ...options, headers })

  // Se 401, prova a rinnovare il token
  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      // Riprova la richiesta con il nuovo token
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`
      response = await fetch(url, { ...options, headers })
    }
  }

  // Gestisci errori
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Errore di rete' }))
    // Se ci sono dettagli di validazione Zod, mostrali
    if (error.details && Array.isArray(error.details)) {
      const messages = error.details.map((d: any) => d.message || d.path?.join('.') || 'Campo non valido').join(', ')
      throw new Error(messages || error.error)
    }
    throw new Error(error.error || `Errore ${response.status}`)
  }

  return response.json()
}

// Metodi helper
export const api = {
  get: <T = any>(endpoint: string) => apiFetch<T>(endpoint),

  post: <T = any>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = any>(endpoint: string, data?: any) =>
    apiFetch<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = any>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'DELETE' }),
}

export default api
